import { createApp, createSSRApp, h } from 'vue';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import createStore from './common/store/index.js';
import initialFilters from './common/filters/index.js';
import initializeRouter from './common/routers/index.js';
import $eventBus from './common/providers/eventbus.js';
import urlMixin from './common/mixins/url.js';
import vnumberMixin from './common/mixins/vnumber.js';
import compNameMixin from './common/mixins/comp.js';
import molang from './common/plugins/molang.js';
import App from './app.vue';
import mvtBreadcrumb from './common/components/mvtbreadcrumb/mvt-breadcrumb.vue';
import mvtHeader from './common/components/mvtheader/mvt-header.vue';
import mvtFooter from './common/components/mvtfooter/mvt-footer.vue';
import mvtHotleadForm from './common/components/mvthotleadform/mvt-hotleadform.vue';
import mvtNearby from './common/components/mvtnearby/mvt-nearby.vue';
import mvtCollapse from './common/components/mvtcollapse/mvt-collapse.vue';
import mvtLazyload from './common/components/mvtlazyload/mvt-lazyload.vue';
import mvtSeoLink from './common/components/mvtseolink/mvt-seolink.vue';
import mvtCardschool from './common/components/mvtcardschool/mvt-cardschool.vue';
import mvtFaq from './common/components/mvtfaq/mvt-faq.vue';

import { fetchCachedClientTreatments, formatSplitsForGA4 } from './common/components/split/utils.js';
import { loadDebugBear } from '@/common/utilities/nojq/_load.thirdparty.scripts.js';

const isSSR = typeof window === 'undefined';

export function importDatadogRUM() {
    return import(/* webpackChunkName: "datadog-rum" */ '@datadog/browser-rum');
}

const getRouter = (routes, defaultPath) =>
    createRouter({
        history: (isSSR ? createMemoryHistory : createWebHistory)(defaultPath),
        routes,
    });

function initDatadogRUM() {
    if (isSSR) {
        return;
    }

    const glb = window.__INITIAL_STATE__;
    if (glb.splits['movoto-enable-datadog-rum-CW-10249'] === 'on' && glb.enableThirdPart) {
        try {
            importDatadogRUM().then(({ datadogRum }) => {
                datadogRum.init({
                    applicationId: 'a5fff77c-4a9e-4f31-8b13-2917d5c2d937',
                    clientToken: 'pub0d7b506a8f86075d8db32685ed779e03',
                    site: 'datadoghq.com',
                    service: 'movoto-web',
                    env: glb.isTenX ? glb.tenXEnv : glb.isDevelopment ? 'development' : 'production',
                    version: glb.version,
                    sessionSampleRate: 100,
                    sessionReplaySampleRate: 0,
                    defaultPrivacyLevel: 'allow',
                    trackViewsManually: true, // we manually track views in the router.beforeEach function in vweb/common/routers/index.js
                    allowedTracingUrls: [
                        /https:\/\/.*\.movoto\.com/,
                        (url) => {
                            return url.includes(glb.appUrl);
                        },
                    ],
                });
                let user = {
                    movotoDeviceId: glb.movotoDeviceID,
                    split: glb.splits ? formatSplitsForGA4(glb.splits, fetchCachedClientTreatments()) : '',
                };
                if (glb.user && glb.user.id && glb.user.id !== '') {
                    user = {
                        ...user,
                        ...glb.user,
                    };
                }
                datadogRum.setUser(user);
                window.ddAddError = datadogRum.addError;
            });
        } catch (err) {
            console.error('An error occured initializing Datadog RUM', err);
        }
    } else if (glb.splits['movoto-enable-datadog-rum-CW-10249'] === 'on1' && glb.enableThirdPart) {
        loadDebugBear();
    }
}

const createVueApp = (model = null, appCreator) => {
    if (!isSSR) {
        initDatadogRUM();
    }

    const store = createStore();
    const router = initializeRouter(getRouter, model);

    const app = appCreator({
        router,
        render: () => h(App),
    });

    app.provide('$eventBus', $eventBus);
    app.use(router);
    app.use(store);

    molang.install(app, store);
    initialFilters.install(app);
    app.config.globalProperties.isSSR = isSSR;

    app.mixin(compNameMixin);
    app.mixin(urlMixin);
    app.mixin(vnumberMixin);
    // https://stackoverflow.com/a/64429013
    app.mixin({
        mounted() {
            this.$el.__vue__ = this; // for __vue__ for backwards compatibility.
        },
    });

    return { app, router, store };
};

export function createSSR(model) {
    let app = createVueApp(model, createSSRApp);
    return app;
}
export function createCSR(model) {
    let app = createVueApp(model, createApp);
    return app;
}
